/**
 * Custom hook for the timetoinvoice integration settings card.
 *
 * Mirrors useRevenueTarget: a write is PUT then refetch. The API key is
 * only ever passed straight through `save` / `test` to the request body —
 * it never lands in hook state and nothing here touches localStorage.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fetchIntegrationStatus,
  saveIntegration,
  testIntegration,
  removeIntegration,
  IntegrationTestFailedError,
  IntegrationForbiddenError,
} from '@/api/integrationsApi';
import type {
  IntegrationStatus,
  IntegrationTestResult,
  SaveIntegrationInput,
  TestIntegrationInput,
} from '@/api/integrationsApi';

const LOAD_FAILED_MESSAGE = 'Could not load integration settings. Refresh to try again.';

export interface UseIntegrationSettingsResult {
  status: IntegrationStatus | null;
  loading: boolean;
  /** Set when the status GET itself failed — the card renders no form */
  loadError: string | null;
  saving: boolean;
  testing: boolean;
  removing: boolean;
  saveError: string | null;
  /** Latest probe outcome, from Test connection or from a refused save */
  testResult: IntegrationTestResult | null;
  /** True when the last save was refused because its probe failed */
  saveBlockedByTest: boolean;
  save: (input: SaveIntegrationInput) => Promise<boolean>;
  test: (input?: TestIntegrationInput) => Promise<IntegrationTestResult | null>;
  remove: () => Promise<boolean>;
  refresh: () => Promise<void>;
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function useIntegrationSettings(): UseIntegrationSettingsResult {
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<IntegrationTestResult | null>(null);
  const [saveBlockedByTest, setSaveBlockedByTest] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      setStatus(await fetchIntegrationStatus());
    } catch (err) {
      // Fail closed: no status means no form, whatever the cause.
      setStatus(null);
      setLoadError(err instanceof IntegrationForbiddenError ? err.message : LOAD_FAILED_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (input: SaveIntegrationInput): Promise<boolean> => {
      try {
        setSaving(true);
        setSaveError(null);
        setSaveBlockedByTest(false);
        await saveIntegration(input);
        setTestResult(null);
        await refresh();
        return true;
      } catch (err) {
        if (err instanceof IntegrationTestFailedError) {
          setTestResult(err.result);
          setSaveBlockedByTest(true);
        } else {
          setSaveError(messageOf(err, 'Could not save the connection.'));
        }
        return false;
      } finally {
        setSaving(false);
      }
    },
    [refresh]
  );

  const test = useCallback(async (input: TestIntegrationInput = {}) => {
    try {
      setTesting(true);
      setSaveError(null);
      setSaveBlockedByTest(false);
      const result = await testIntegration(input);
      setTestResult(result);
      return result;
    } catch (err) {
      // Clear any earlier result so a failed test never renders alongside
      // a stale success/failure message from a previous attempt.
      setTestResult(null);
      setSaveError(messageOf(err, 'Could not test the connection.'));
      return null;
    } finally {
      setTesting(false);
    }
  }, []);

  const remove = useCallback(async (): Promise<boolean> => {
    try {
      setRemoving(true);
      setSaveError(null);
      setSaveBlockedByTest(false);
      setTestResult(null);
      setStatus(await removeIntegration());
      return true;
    } catch (err) {
      setSaveError(messageOf(err, 'Could not remove the connection.'));
      return false;
    } finally {
      setRemoving(false);
    }
  }, []);

  return {
    status,
    loading,
    loadError,
    saving,
    testing,
    removing,
    saveError,
    testResult,
    saveBlockedByTest,
    save,
    test,
    remove,
    refresh,
  };
}
